import React, { useCallback, useMemo } from 'react';
import { t, Trans } from '@lingui/macro';
import { postApiFormData } from 'util/backendapi/fetch';
import { downloadFile } from 'util/backendapi/download';
import { useIsMounted } from 'util/hooks';
import { FormikHelpers, FormikErrors, Formik, Form } from 'formik';
import { FormSection, FormItem } from 'components/base/form/FormItem';
import ErrorNotice, {
  FieldError,
} from 'components/base/form/errornotice/errornotice';
import Button, { ButtonPrimary } from 'components/base/button/button';
import { FileField } from 'components/base/form/file-field/FileField';
import ModalContent from 'components/base/modal/modalcontent';
import ActionBlock from 'components/base/actionblock/actionblock';
import ButtonHideModal from 'components/base/modal/buttonhidemodal';
import { useHistory } from 'react-router';
import { ObservationPointDecorated } from 'util/backendapi/types/Model';
import {
  bulkCreateError,
  clearBulkCreateErrors,
} from 'ducks/bulk-create/errors';
import { useDispatch } from 'react-redux';
import { showErrorsInFormik } from 'util/backendapi/error-formik';
import { getExpectedFields } from 'util/backendapi/error';
import { SimpleSelectField } from 'components/base/form/simpleselect/simpleselectfield';
import { SimpleSelectOption } from 'components/base/form/simpleselect/simpleselect';
import { useGetApi } from 'hooks/use-get-api';
import { i18n } from '@lingui/core';
import sortBy from 'lodash/sortBy';
import Loading from 'components/base/loading/loading';
type FormValues = {
  file_name: string;
  attached_file: File | null;
  formula: number;
};

interface Props {
  onAfterSubmit: () => void;
}

export function ObsPointBulkCreateModal({ onAfterSubmit }: Props) {
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const history = useHistory();

  const validate = useCallback(
    (values: FormValues): FormikErrors<FormValues> => {
      const errors: FormikErrors<FormValues> = {};
      if (!values.file_name) {
        errors.file_name = (<Trans>A file is required.</Trans>) as any;
      }
      return errors;
    },
    []
  );

  const handleSubmit = useCallback(
    async (values: FormValues, formik: FormikHelpers<FormValues>) => {
      const formData = new FormData();

      if (values.file_name && values.attached_file) {
        formData.append('file', values.attached_file, values.file_name);
      }

      if (values.formula > 0) {
        formData.append('formula', String(values.formula));
      }

      try {
        const result = await postApiFormData(
          '/observation-points/bulk-create/',
          formData
        );

        const newObservationPointIds = result
          .map(
            (observationPoint: ObservationPointDecorated) => observationPoint.id
          )
          .join(',');

        dispatch(clearBulkCreateErrors());
        history.push(`/observation-point/?id=${newObservationPointIds}`);
        onAfterSubmit();
      } catch (err) {
        if (isMounted()) {
          formik.setSubmitting(false);
          if (err.content) {
            await dispatch(bulkCreateError(err));
            history.push(`/observation-point/bulk-create/`);
          } else if (err instanceof Error) {
            formik.setFieldError(
              'file_name',
              'The upload was not successful. Please try again.'
            );
          } else {
            showErrorsInFormik(
              formik,
              err,
              getExpectedFields(values),
              ErrorNotice
            );
          }
        }
      }
    },
    [onAfterSubmit, isMounted, dispatch, history]
  );

  const initialValues: FormValues | null = useMemo(() => {
    return {
      attached_file: null,
      file_name: '',
      formula: 0,
    };
  }, []);

  const [formulas] = useGetApi('/formulas/', {
    code__in: ['DIFF', 'IPI', 'ROUND', 'SUM'],
  });

  const formulaOptions: SimpleSelectOption<number>[] = useMemo(() => {
    let options = [{ label: i18n._(t`No formula`), value: 0 }];
    if (!formulas.data) return options;
    return options.concat(
      sortBy(
        formulas.data.map((formula) => ({
          label: formula.code,
          value: formula.id,
        })),
        'label'
      )
    );
  }, [formulas.data]);

  return (
    <ModalContent header={<Trans>Bulk create observation points</Trans>}>
      <Formik<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validate}
      >
        {(formik) => (
          <Form>
            <div
              id="bulk-create-observation-points-modal"
              className="bulk-create-modal"
            >
              {formik.status}
              <FormSection label={<Trans>Formula</Trans>}>
                {formulas.isLoading ? (
                  <Loading />
                ) : (
                  <SimpleSelectField<number>
                    options={formulaOptions}
                    name="formula"
                    isDisabled={formik.isSubmitting}
                  />
                )}
                <p>
                  <Trans>
                    Specific columns will be added to the CSV template for the
                    selected formula and all uploaded observation points will be
                    assigned that formula.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    Select "No formula" to not assign a formula to the uploaded
                    observation points.
                  </Trans>
                </p>
              </FormSection>
              <FormSection>
                <Button
                  onClick={() =>
                    downloadFile(
                      `/observation-points/bulk-create-template/?formula=${
                        formik.values.formula || ''
                      }`
                    )
                  }
                >
                  <Trans>Download observation point CSV template</Trans>
                </Button>
              </FormSection>
              <FormSection>
                <Trans>
                  Please note:
                  <ul>
                    <li>
                      The sites and area(s) must be created in the DMS before
                      uploading the completed observation point CSV.
                    </li>
                    <li>
                      Dates and times must be entered in the following format:
                      dd/mm/yyyy hh:mm
                    </li>
                    <li>
                      All dates and times will be saved in the area's timezone.
                    </li>
                    <li>
                      The details in the CSV will be validated and any errors
                      will be displayed on screen. If no errors are found all of
                      the observation points will be created.
                    </li>
                    <li>
                      If no errors are found all of the observation points will
                      be created.
                    </li>
                  </ul>
                </Trans>
              </FormSection>
              <FormSection>
                <FormItem
                  label={<Trans>Upload complete observation point CSV</Trans>}
                >
                  <div className="file-input-wrapper">
                    <FileField
                      name="file_name"
                      fileFieldName="attached_file"
                      fileExtensions={['.csv']}
                    />
                  </div>
                  <FieldError name="file_name" />
                </FormItem>
              </FormSection>
              <ActionBlock>
                <ButtonHideModal />
                <ButtonPrimary
                  data-testid="bulk-create-observation-points-submit"
                  type="submit"
                  disabled={formik.isSubmitting}
                  iconType="icon-upload"
                >
                  <Trans>Upload</Trans>
                </ButtonPrimary>
              </ActionBlock>
            </div>
          </Form>
        )}
      </Formik>
    </ModalContent>
  );
}
